// =============================================================================
// quoteFormat.ts — display helpers for quote numbers and revision labels.
// T-244 (Phase 1 of T-123). Used everywhere a quote_number is rendered so the
// R-suffix is consistent across UI, list, builder, PDF, exports.
// =============================================================================

/**
 * Format a quote's display number with the R-suffix when it's a revision.
 *
 * Rules:
 *   - revision_number === 1 (original)  → "Q-26-0042"
 *   - revision_number === 2 (1st rev)   → "Q-26-0042-R1"
 *   - revision_number === 3 (2nd rev)   → "Q-26-0042-R2"
 *
 * The DB stores the same `quote_number` across all revisions of a root; the
 * display layer adds the suffix purely for readability.
 *
 * Variations also flow through this helper. A first-time variation has its own
 * VAR-YY-NNNN with revision_number=1 → no suffix. A revised variation gets
 * the same suffix treatment (e.g. VAR-26-0001-R1).
 */
export function formatQuoteNumber(
  q: { quote_number: string | null | undefined; revision_number: number | null | undefined }
): string {
  const num = q.quote_number ?? "";
  const rev = q.revision_number ?? 1;
  if (!num) return "";
  if (rev > 1) return `${num}-R${rev - 1}`;
  return num;
}

/**
 * Short revision label e.g. "R2", or "" for the original (revision_number=1).
 * Useful for compact pills/badges where the full number is shown elsewhere.
 */
export function revisionLabel(
  q: { revision_number: number | null | undefined }
): string {
  const rev = q.revision_number ?? 1;
  return rev > 1 ? `R${rev - 1}` : "";
}
